import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Clock, ArrowUpRight, Star, Loader2 } from "lucide-react";
import Seo from "@/components/Seo";
import { formatarData } from "@/data/artigosData";
import { getJSON } from "@/lib/api";

const PAGE_SIZE = 6;
const ETAPAS_FIXAS = ["Planejamento", "Gestação", "Parto", "Pós-parto"];

const ArticleCard = ({ a, featured }) => (
  <motion.div
    initial={{ opacity: 0, y: 22 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
  >
    <Link
      to={`/conteudos/${a.slug}`}
      data-testid={`artigo-card-${a.id}`}
      className="group block bg-white rounded-3xl overflow-hidden border border-maternnia-nevoa/60 shadow-sm hover:shadow-[0_16px_40px_rgba(43,37,38,0.08)] hover:-translate-y-1 transition-all duration-300 h-full"
    >
      <div className={`relative overflow-hidden ${a.imgPortrait ? "aspect-[4/5]" : featured ? "aspect-[16/9]" : "aspect-[4/3]"}`}>
        <img src={a.imagem} alt={a.imagemAlt} loading="lazy" className={`w-full h-full object-cover ${a.imgPos || ""} group-hover:scale-105 transition-transform duration-500`} />
        <span className="absolute top-4 left-4 text-xs font-semibold tracking-wide uppercase bg-white/90 text-maternnia-charcoal px-3 py-1.5 rounded-full">{a.etapa}</span>
      </div>
      <div className="p-7">
        <span className="text-xs font-semibold tracking-wide text-maternnia-eucalipto uppercase">{a.categoria}</span>
        <h3 className="font-serif text-2xl text-maternnia-charcoal mt-2 leading-snug font-medium group-hover:text-maternnia-eucalipto transition-colors">{a.titulo}</h3>
        <p className="mt-3 text-sm text-maternnia-taupe leading-relaxed font-light line-clamp-2">{a.resumo}</p>
        <div className="mt-5 flex items-center justify-between text-xs text-maternnia-taupe">
          <span>{a.data ? formatarData(a.data) : ""}</span>
          <span className="inline-flex items-center gap-1"><Clock size={13} /> {a.tempoLeitura}</span>
        </div>
      </div>
    </Link>
  </motion.div>
);

const ConteudosPage = () => {
  const [termo, setTermo] = useState("");
  const [etapa, setEtapa] = useState("");
  const [categoria, setCategoria] = useState("");
  const [visiveis, setVisiveis] = useState(PAGE_SIZE);
  const [artigos, setArtigos] = useState(null);

  useEffect(() => {
    getJSON("/conteudos").then(setArtigos).catch(() => setArtigos([]));
  }, []);

  const destaques = useMemo(() => (artigos || []).filter((a) => a.destaque).slice(0, 3), [artigos]);
  const categorias = useMemo(() => [...new Set((artigos || []).map((a) => a.categoria).filter(Boolean))].sort(), [artigos]);

  const filtrados = useMemo(() => {
    const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    return (artigos || []).filter((a) => {
      if (termo && !norm(`${a.titulo} ${a.resumo}`).includes(norm(termo))) return false;
      if (etapa && a.etapa !== etapa) return false;
      if (categoria && a.categoria !== categoria) return false;
      return true;
    });
  }, [termo, etapa, categoria, artigos]);

  const temFiltro = termo || etapa || categoria;
  const lista = filtrados.slice(0, visiveis);

  return (
    <main data-testid="conteudos-page" className="bg-white min-h-screen">
      <Seo
        title="Conteúdos e artigos"
        description="Artigos e conteúdos confiáveis sobre planejamento, gestação, parto e pós-parto, organizados por etapa da maternidade."
      />
      {/* HEADER */}
      <section className="bg-maternnia-porcelana">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <span className="text-sm font-semibold tracking-widest text-maternnia-pink uppercase">Conteúdos</span>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-maternnia-charcoal mt-4 font-medium">
            Conteúdos e artigos
          </h1>
          <p className="mt-5 text-base md:text-lg text-maternnia-taupe leading-relaxed font-light max-w-2xl">
            Informação confiável e organizada por etapa para apoiar suas decisões — do planejamento ao pós-parto.
          </p>
        </div>
      </section>

      {artigos === null ? (
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-28 flex justify-center"><Loader2 className="animate-spin text-maternnia-eucalipto" size={28} /></div>
      ) : (
      <>
      {/* DESTAQUES */}
      {!temFiltro && (
        <section data-testid="conteudos-destaques" className="max-w-7xl mx-auto px-6 md:px-12 pt-14 md:pt-16">
          <div className="flex items-center gap-2 mb-7">
            <Star size={18} className="text-maternnia-gold" />
            <h2 className="font-serif text-2xl md:text-3xl text-maternnia-charcoal font-medium">Em destaque</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-7">
            {destaques.map((a) => <ArticleCard key={a.id} a={a} featured />)}
          </div>
        </section>
      )}

      {/* FILTROS + LISTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-16">
        <div className="bg-maternnia-porcelana/60 rounded-3xl p-5 md:p-6 mb-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative md:col-span-1">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-maternnia-taupe" />
            <input
              data-testid="conteudos-busca" type="text" value={termo}
              onChange={(e) => { setTermo(e.target.value); setVisiveis(PAGE_SIZE); }}
              placeholder="Buscar artigos..."
              className="w-full bg-white border border-maternnia-nevoa rounded-xl pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-maternnia-pink/30 focus:border-maternnia-pink transition"
            />
          </div>
          <select data-testid="conteudos-filtro-etapa" value={etapa} onChange={(e) => { setEtapa(e.target.value); setVisiveis(PAGE_SIZE); }}
            className="bg-white border border-maternnia-nevoa rounded-xl px-4 py-3 text-sm text-maternnia-charcoal focus:outline-none focus:ring-2 focus:ring-maternnia-pink/30 focus:border-maternnia-pink transition">
            <option value="">Todas as etapas</option>
            {ETAPAS_FIXAS.map((x) => <option key={x}>{x}</option>)}
          </select>
          <select data-testid="conteudos-filtro-categoria" value={categoria} onChange={(e) => { setCategoria(e.target.value); setVisiveis(PAGE_SIZE); }}
            className="bg-white border border-maternnia-nevoa rounded-xl px-4 py-3 text-sm text-maternnia-charcoal focus:outline-none focus:ring-2 focus:ring-maternnia-pink/30 focus:border-maternnia-pink transition">
            <option value="">Todas as categorias</option>
            {categorias.map((x) => <option key={x}>{x}</option>)}
          </select>
        </div>

        <p data-testid="conteudos-count" className="text-sm text-maternnia-taupe font-medium mb-7">
          {filtrados.length} artigo(s) encontrado(s)
        </p>

        {filtrados.length === 0 ? (
          <div data-testid="conteudos-vazio" className="text-center py-20">
            <h3 className="font-serif text-2xl text-maternnia-charcoal font-medium">Nenhum artigo encontrado</h3>
            <p className="mt-2 text-sm text-maternnia-taupe font-light">Tente outra busca ou ajuste os filtros.</p>
          </div>
        ) : (
          <>
            <div data-testid="conteudos-lista" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
              {lista.map((a) => <ArticleCard key={a.id} a={a} />)}
            </div>
            {visiveis < filtrados.length && (
              <div className="text-center mt-12">
                <button
                  data-testid="conteudos-carregar-mais"
                  onClick={() => setVisiveis((v) => v + PAGE_SIZE)}
                  className="group inline-flex items-center gap-2 border border-maternnia-charcoal/20 text-maternnia-charcoal hover:bg-maternnia-charcoal hover:text-white rounded-full px-8 py-3.5 text-sm font-medium transition-all duration-300"
                >
                  Carregar mais
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </div>
            )}
          </>
        )}
      </section>
      </>
      )}
    </main>
  );
};

export default ConteudosPage;
